import { useEffect, useRef, useState } from 'react';
import { PERMISSIONS } from '@oxxean/types';
import { downloadExport, EXPORT_FORMAT_LABEL, EXPORT_FORMATS, type ExportFormat } from '../lib/api/exports';
import { errorMessage } from '../lib/errors';
import { useAuth } from '../hooks/useAuth';
import { useFilters } from '../hooks/useFilters';
import { useToasts } from '../hooks/useToasts';
import { Button } from './primitives';
import { Icon } from './Icon';

/**
 * Menú de exportación.
 *
 * Sólo se renderiza con `exports.execute`; el endpoint vuelve a comprobarlo
 * (ocultar un botón es cosmética, principio 6). Los filtros globales vigentes
 * viajan con la solicitud para que el archivo coincida con lo que se ve en
 * pantalla.
 */
export function ExportMenu({ moduleKey }: { moduleKey: string }): JSX.Element | null {
  const { can } = useAuth();
  const { filters } = useFilters();
  const { push } = useToasts();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  if (!can(PERMISSIONS.EXPORTS_EXECUTE)) return null;

  const run = async (format: ExportFormat): Promise<void> => {
    setBusy(format);
    try {
      await downloadExport(moduleKey, format, filters);
      push({
        kind: 'success',
        title: 'Exportación generada',
        message: 'El archivo incluye los metadatos de procedencia del período consultado.',
      });
      setOpen(false);
    } catch (error) {
      push({ kind: 'error', title: 'No se pudo exportar', message: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="popover" ref={containerRef}>
      <Button
        icon="download"
        size="sm"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
        loading={busy !== null}
      >
        Exportar
      </Button>
      {open && (
        <div className="popover__panel" role="menu" aria-label="Formatos de exportación">
          {EXPORT_FORMATS.map((format) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              className="popover__item"
              disabled={busy !== null}
              onClick={() => void run(format)}
            >
              <Icon name="download" size={14} />
              {EXPORT_FORMAT_LABEL[format]}
            </button>
          ))}
          <div className="popover__sep" />
          <p className="tiny muted" style={{ padding: '2px 10px 6px' }}>
            Se exporta con los filtros vigentes y con la procedencia del dato adjunta.
          </p>
        </div>
      )}
    </div>
  );
}
